// Global Instructions Rule Applied!
//
// User rights on screen (docs/USER_RIGHTS_DESIGN.md, section 5.4): may the
// signed-in user do this, and if not, why not?
//
// Reads globalConfigs.capabilities (public.view__my_capabilities, resolved per
// request). A display hint only - public.require_capability() refuses in the
// database whatever the screen shows - so this decides whether a control is
// enabled (disabled with `reason` in a Tooltip) or shown at all.
//
// Fails CLOSED: a capability the payload does not carry (a backend that
// predates it, or a failed read) is not allowed, with a reason that says so.
//
//   const { allowed, reason } = useCapability('asset_transactions.book')
//   const can = useCapabilityCheck()          // for a list: one check per row
//   can('pipeline_stage.move', column.id, column.name)
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import type { RootState } from '../../state_manager/store.ts'
import type { CapabilityGrantModel } from '../../state_manager/data_models/capability_grant_model.ts'

export interface CapabilityCheck {
  allowed: boolean
  // The database's sentence, e.g. "Only Admin can book asset transactions."
  // null when allowed.
  reason: string | null
}

export type CapabilityChecker = (
  capabilityKey: string,
  scopeId?: string | null,
  recordLabel?: string | null
) => CapabilityCheck

// An override's reason names its record with this placeholder; the screen fills it.
export const RECORD_PLACEHOLDER = '{record}'
const RECORD_FALLBACK = 'this record'

export const CAPABILITIES_NOT_LOADED = 'Your permissions could not be loaded. Refresh the page, or ask an administrator.'

export function resolveCapability(
  grants: CapabilityGrantModel[] | null | undefined,
  capabilityKey: string,
  scopeId?: string | null,
  recordLabel?: string | null
): CapabilityCheck {
  const grant = grants?.find((g) => g.capability_key === capabilityKey)
  if (!grant) return { allowed: false, reason: CAPABILITIES_NOT_LOADED }

  const override = scopeId ? grant.overrides.find((o) => o.scope_id === scopeId) : undefined
  if (override) {
    if (override.allowed) return { allowed: true, reason: null }
    const reason = override.reason ?? grant.reason ?? null
    return { allowed: false, reason: reason ? reason.split(RECORD_PLACEHOLDER).join(recordLabel || RECORD_FALLBACK) : null }
  }

  return grant.allowed ? { allowed: true, reason: null } : { allowed: false, reason: grant.reason ?? null }
}

export function useCapabilityCheck(): CapabilityChecker {
  const grants = useSelector((state: RootState) => state.globalConfigs?.capabilities)
  return useCallback<CapabilityChecker>(
    (capabilityKey, scopeId, recordLabel) => resolveCapability(grants, capabilityKey, scopeId, recordLabel),
    [grants]
  )
}

export function useCapability(
  capabilityKey: string,
  scopeId?: string | null,
  recordLabel?: string | null
): CapabilityCheck {
  const check = useCapabilityCheck()
  return useMemo(() => check(capabilityKey, scopeId, recordLabel), [check, capabilityKey, scopeId, recordLabel])
}
